import { ChartCard } from "@/components/v2/ChartCard";
import { ProviderTable, type ProviderTableRow } from "@/components/v2/ProviderTable";
import { SectionBlock } from "@/components/v2/SectionBlock";
import { StackedProviderAreaChart } from "@/components/v2/charts";
import { useProviderHistory, useProviderSummary } from "@/hooks/useHip3Analytics";
import { shapeProviderDominanceSeries } from "@/lib/analytics-transforms";
import { useMemo } from "react";

export function ProvidersPage() {
  const summaryQuery = useProviderSummary({ window: "7d", sort: "oiShare" });
  const historyQuery = useProviderHistory({ metric: "oiShare", window: "30d", interval: "4h" });

  const areaData = useMemo(
    () => shapeProviderDominanceSeries(historyQuery.data?.series ?? []),
    [historyQuery.data?.series]
  );

  const providerKeys = useMemo(
    () => historyQuery.data?.series.map((series) => series.provider) ?? [],
    [historyQuery.data?.series]
  );

  const rows: ProviderTableRow[] = (summaryQuery.data?.providers ?? []).map((provider) => ({
    provider: provider.provider,
    markets: provider.markets,
    oiShare: provider.oiShare,
    volumeShare: provider.volumeShare,
    oiChangePct: provider.oiChangePct,
    spreadAvgBps: provider.spreadAvgBps,
    fundingMean: provider.fundingMean,
  }));

  return (
    <div className="space-y-6">
      <SectionBlock
        title="Providers"
        subtitle="Understand provider footprint, market coverage, and ecosystem share dynamics."
      >
        <ChartCard title="Provider OI Share Over Time" loading={historyQuery.isLoading}>
          <StackedProviderAreaChart data={areaData} providers={providerKeys} />
        </ChartCard>
      </SectionBlock>

      <SectionBlock title="Provider Summary" subtitle="Current market and liquidity footprint for each provider.">
        <ProviderTable rows={rows} />
      </SectionBlock>
    </div>
  );
}
